import { useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { hasAccents, textRuns, type TextAccent, type TextLayer } from '@bannermatic/core';
import { fontStack } from '@bannermatic/core/fonts';
import { fitText, REFIT_EVENT } from '../autofit';
import { fillStyle, textShadow } from '../style';

const JUSTIFY: Record<TextLayer['verticalAlign'], CSSProperties['justifyContent']> = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
};

export interface TextLayerViewProps {
  layer: TextLayer;
  editing?: boolean;
  onCommit?: (text: string) => void;
  onEditInput?: () => void;
}

export function TextLayerView({ layer, editing, onCommit, onEditInput }: TextLayerViewProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  const refit = useCallback(() => {
    const box = boxRef.current;
    const text = textRef.current;
    if (!box || !text) return;
    if (layer.autoFit) fitText(box, text, layer.fontSize, layer.minFontSize);
    else text.style.fontSize = `${layer.fontSize}px`;
  }, [layer]);

  // Font size is owned by this effect (not by React style) so auto-fit can adjust it.
  useLayoutEffect(() => {
    refit();
  }, [refit, editing]);

  useEffect(() => {
    window.addEventListener(REFIT_EVENT, refit);
    return () => window.removeEventListener(REFIT_EVENT, refit);
  }, [refit]);

  // Inline editing: populate the editable node once, focus it and select all.
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!editing || !el) return;
    // The accented words keep their look while typing (asterisks of `**markup**` stay visible)
    if (hasAccents(layer)) {
      el.replaceChildren(
        ...textRuns(layer, { markup: false }).map((run) => {
          if (!run.style) return document.createTextNode(run.text);
          const span = document.createElement('span');
          span.className = 'bm-accent';
          Object.assign(span.style, accentStyle(run.style));
          span.textContent = run.text;
          return span;
        }),
      );
    } else el.innerText = layer.text;
    refit();
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const clamp = layer.maxLines > 0;
  const boxStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: JUSTIFY[layer.verticalAlign],
    paddingTop: layer.padding.top,
    paddingRight: layer.padding.right,
    paddingBottom: layer.padding.bottom,
    paddingLeft: layer.padding.left,
    borderRadius: layer.borderRadius || undefined,
    boxSizing: 'border-box',
    overflow: layer.autoFit || clamp ? 'hidden' : 'visible',
    ...fillStyle(layer.background),
  };

  const textStyle: CSSProperties = {
    flex: '0 0 auto',
    width: '100%',
    margin: 0,
    fontFamily: fontStack(layer.fontFamily),
    fontWeight: layer.fontWeight,
    fontStyle: layer.fontStyle,
    color: layer.color,
    lineHeight: layer.lineHeight,
    letterSpacing: layer.letterSpacing ? `${layer.letterSpacing}px` : undefined,
    textAlign: layer.align,
    textTransform: layer.textTransform === 'none' ? undefined : layer.textTransform,
    textDecoration: layer.textDecoration === 'none' ? undefined : layer.textDecoration,
    whiteSpace: 'pre-wrap',
    overflowWrap: layer.autoFit ? 'normal' : 'break-word',
    wordBreak: 'normal',
    WebkitTextStroke:
      layer.strokeWidth > 0 ? `${layer.strokeWidth}px ${layer.strokeColor}` : undefined,
    paintOrder: layer.strokeWidth > 0 ? 'stroke fill' : undefined,
    textShadow: textShadow(layer.shadow),
    fontKerning: 'normal',
    textRendering: 'geometricPrecision',
    outline: 'none',
    cursor: editing ? 'text' : undefined,
    userSelect: editing ? 'text' : 'none',
    ...(clamp
      ? {
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: layer.maxLines,
          overflow: 'hidden',
        }
      : {}),
  };

  return (
    <div ref={boxRef} className="bm-text-box" style={boxStyle}>
      {editing ? (
        <div
          key="editing"
          ref={textRef}
          className="bm-text"
          data-clamped={clamp ? 'true' : undefined}
          style={textStyle}
          contentEditable
          suppressContentEditableWarning
          spellCheck={false}
          onInput={() => {
            refit();
            onEditInput?.();
          }}
          onBlur={(e) => onCommit?.(typedText(e.currentTarget as HTMLDivElement))}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') (e.currentTarget as HTMLDivElement).blur();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <div
          key="static"
          ref={textRef}
          className="bm-text"
          data-clamped={clamp ? 'true' : undefined}
          style={textStyle}
        >
          {(() => {
            const content = hasAccents(layer) ? <AccentedText layer={layer} /> : layer.text;
            return layer.highlight ? (
              <span
                className="bm-text-highlight"
                style={{
                  background: layer.highlight.color,
                  padding: `${layer.highlight.paddingY}px ${layer.highlight.paddingX}px`,
                  borderRadius: layer.highlight.radius || undefined,
                  boxDecorationBreak: 'clone',
                  WebkitBoxDecorationBreak: 'clone',
                }}
              >
                {content}
              </span>
            ) : (
              content
            );
          })()}
        </div>
      )}
    </div>
  );
}

/** The text with its accented words (picked in the editor, `**marked**`, numbers). */
function AccentedText({ layer }: { layer: TextLayer }) {
  return (
    <>
      {textRuns(layer).map((run, i) =>
        run.style ? (
          <span key={i} className="bm-accent" style={accentStyle(run.style)}>
            {run.text}
          </span>
        ) : (
          run.text
        ),
      )}
    </>
  );
}

function accentStyle(a: TextAccent): CSSProperties {
  return {
    color: a.color,
    fontWeight: a.fontWeight,
    fontStyle: a.fontStyle,
    textDecoration: a.textDecoration && a.textDecoration !== 'none' ? a.textDecoration : undefined,
    // Relative, so auto-fit shrinking the text shrinks the word with it
    fontSize: a.scale && a.scale !== 1 ? `${a.scale}em` : undefined,
    ...(a.background
      ? {
          background: a.background,
          padding: '0 0.12em',
          borderRadius: '0.15em',
          boxDecorationBreak: 'clone',
          WebkitBoxDecorationBreak: 'clone',
        }
      : {}),
  };
}

/**
 * The text as typed: `innerText` applies `text-transform`, so an upper-case style would save
 * the words in capitals.
 */
function typedText(el: HTMLElement): string {
  const transform = el.style.textTransform;
  el.style.textTransform = 'none';
  const text = el.innerText;
  el.style.textTransform = transform;
  return text.replace(/\n$/, '');
}
